import React, { useContext } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { UserData, UserContext } from '../UserAndProfile/UserContext';

interface ReferralProgressItemProps {
  referral: UserData['referredUsers'][0];
}

const ReferralProgressItem: React.FC<ReferralProgressItemProps> = ({ referral }) => {
  const { resendReferralInvitation } = useContext(UserContext);

  const getInitials = (firstName: string, lastName?: string | null) => {
    const first = firstName.charAt(0).toUpperCase();
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'invited':
        return 'default';
      case 'signed_up':
        return 'primary';
      case 'active':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'invited':
        return 'Invited';
      case 'signed_up':
        return 'Signed Up';
      case 'active':
        return 'Active';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleResendInvitation = async () => {
    try {
      await resendReferralInvitation(referral.referralId);
    } catch (error) {
      console.error('Error resending invitation:', error);
    }
  };

  const renderProgressInfo = () => {
    if (referral.referralStatus === 'invited') {
      return (
        <Typography variant="caption" color="text.secondary">
          Invited {formatDate(referral.invitedDate)}
        </Typography>
      );
    }

    const progressItems = [];
    
    if (referral.hasCompletedDiagnostic) {
      progressItems.push('✓ Diagnostic');
    }
    
    if (referral.totalQuestions && referral.totalQuestions > 0) {
      progressItems.push(`${referral.totalQuestions} questions`);
    }
    
    if (referral.fullTestScores && referral.fullTestScores.length > 0) {
      const latestScore = referral.fullTestScores[referral.fullTestScores.length - 1];
      const totalScore = latestScore.mathScore + latestScore.readingWritingScore;
      progressItems.push(`Last test: ${totalScore}`);
    }

    if (progressItems.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          Signed up {referral.signupDate ? formatDate(referral.signupDate) : ''}
        </Typography>
      );
    }

    return (
      <Typography variant="caption" color="text.secondary">
        {progressItems.join(', ')}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        '&:hover': {
          backgroundColor: 'custom.beigeLightContrast',
        },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.875rem',
          bgcolor: 'primary.main',
          mr: 2,
        }}
      >
        {getInitials(referral.firstName, referral.lastName)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mr: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {referral.firstName} {referral.lastName || ''}
          </Typography>
          <Chip
            label={getStatusText(referral.referralStatus)}
            size="small"
            color={getStatusColor(referral.referralStatus)}
            sx={{ fontSize: '0.75rem', height: 20 }}
          />
        </Box>
        
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {referral.email}
        </Typography>
        
        {renderProgressInfo()}
      </Box>

      {referral.referralStatus === 'invited' && (
        <Box sx={{ ml: 1 }}>
          <Tooltip title="Resend invitation">
            <IconButton
              size="small"
              onClick={handleResendInvitation}
              sx={{ color: 'text.secondary' }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default ReferralProgressItem; 